import React, { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import { DialogActions, DialogTitle, DialogContent } from '@material-ui/core';
import { KakarikoButtonMedium, KakarikoButtonSmall } from 'library';
import { StyledKakarikoDialog } from './styles';
import type { IKakarikoDialogProps, IKakarikoConfirmationProps } from './types';

export const KakarikoDialog = (props: IKakarikoDialogProps) => {
  const {
    bannerText,
    buttonTheme = 'primary',
    cancelText = 'Cancel',
    children,
    disabled,
    isLoading,
    errors,
    open,
    successBannerText,
    successModal,
    successTitleText,
    submitText = 'Submit',
    title,
    handleCancel,
    handleClose,
    handleSubmit,
  } = props;

  const hasAnyErrors = useCallback(() => {
    if (errors === undefined) return false;
    const hasErrors = Object.values(errors).some((error: boolean) => {
      return !!error;
    });

    return hasErrors !== undefined ? hasErrors : true;
  }, [errors]);

  const [isSuccessOpen, setSuccessOpen] = useState(false);

  const internalSubmit = async () => {
    try {
      await handleSubmit?.();
      if (successModal) setSuccessOpen(true);
    } catch {} // eslint-disable-line no-empty
  };

  return (
    <>
      <StyledKakarikoDialog open={open} onClose={handleClose}>
        <span className="title-wrapper">
          <DialogTitle>{title}</DialogTitle>
          <KakarikoButtonSmall
            name="close"
            buttonRole="link"
            handleClick={handleClose}
          >
            <CloseIcon />
          </KakarikoButtonSmall>
        </span>
        <DialogContent>{children}</DialogContent>
        {(handleCancel || handleSubmit) && (
          <DialogActions>
            {handleCancel && (
              <KakarikoButtonMedium
                buttonTheme={buttonTheme}
                buttonRole="secondary"
                disabled={isLoading}
                handleClick={() => handleCancel()}
              >
                {cancelText}
              </KakarikoButtonMedium>
            )}
            {handleSubmit && (
              <KakarikoButtonMedium
                buttonTheme={buttonTheme}
                disabled={disabled || hasAnyErrors() || isLoading}
                handleClick={() => internalSubmit()}
                isLoading={isLoading}
              >
                {submitText}
              </KakarikoButtonMedium>
            )}
          </DialogActions>
        )}
        {bannerText && <div className="banner-bar">{bannerText}</div>}
      </StyledKakarikoDialog>
      {successModal && (
        <KakarikoDialog
          handleClose={() => setSuccessOpen(false)}
          open={isSuccessOpen}
          bannerText={successBannerText}
          title={successTitleText}
        >
          {successModal}
        </KakarikoDialog>
      )}
    </>
  );
};

export const KakarikoConfirmation = (props: IKakarikoConfirmationProps) => {
  const {
    bannerText,
    buttonTheme = 'primary',
    cancelText,
    confirmation,
    confirmBannerText,
    confirmText = 'Confirm',
    confirmTitle,
    dialog,
    disabled,
    isLoading,
    errors,
    open,
    returnText = 'Go back',
    submitText = 'Next',
    successBannerText,
    successModal,
    successTitleText,
    title,
    handleCancel,
    handleClose,
    handleSubmit,
  } = props;

  const [primaryKakarikoButtonText, setPrimaryKakarikoButtonText] =
    useState(submitText);
  const [secondaryKakarikoButtonText, setSecondaryKakarikoButtonText] =
    useState(cancelText);
  const [titleText, setTitleText] = useState(title);
  const [bannerTextDisplay, setBannerTextDisplay] = useState(bannerText);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelFn, setCancelFn] = useState<(() => void) | undefined>(undefined);

  const close = useCallback(() => {
    setConfirmOpen(false);
  }, [setConfirmOpen]);

  useEffect(() => {
    if (confirmOpen) {
      setPrimaryKakarikoButtonText(confirmText);
      setSecondaryKakarikoButtonText(returnText);
      setTitleText(confirmTitle ?? title);
      setBannerTextDisplay(confirmBannerText);
      setCancelFn(() => close);
    } else {
      setBannerTextDisplay(bannerText);
      setPrimaryKakarikoButtonText(submitText);
      setSecondaryKakarikoButtonText(cancelText);
      setTitleText(title);
      setCancelFn(() => handleCancel);
    }
  }, [
    bannerText,
    cancelText,
    confirmOpen,
    confirmText,
    confirmTitle,
    confirmBannerText,
    returnText,
    submitText,
    title,
    close,
    handleCancel,
  ]);

  const closeConfirmation = () => {
    handleClose();
  };

  useEffect(() => {
    if (open) {
      setConfirmOpen(false);
      setCancelFn(() => handleCancel);
      setPrimaryKakarikoButtonText(submitText);
      setSecondaryKakarikoButtonText(cancelText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelText, open, submitText]);

  const [isSuccessOpen, setSuccessOpen] = useState(false);

  const internalSubmit = async () => {
    try {
      await handleSubmit?.();
      if (successModal) setSuccessOpen(true);
    } catch {} // eslint-disable-line no-empty
  };

  const openConfirmation = () => {
    if (confirmOpen) {
      internalSubmit();
      setCancelFn(() => handleCancel);
    } else {
      setConfirmOpen(true);
    }
  };

  return (
    <>
      <KakarikoDialog
        bannerText={bannerTextDisplay}
        buttonTheme={buttonTheme}
        cancelText={secondaryKakarikoButtonText}
        disabled={disabled}
        errors={errors}
        handleCancel={cancelFn}
        handleClose={closeConfirmation}
        handleSubmit={() => openConfirmation()}
        isLoading={isLoading}
        open={open}
        submitText={primaryKakarikoButtonText}
        title={titleText}
      >
        {!confirmOpen && dialog}
        {confirmOpen && confirmation}
      </KakarikoDialog>
      {successModal && (
        <KakarikoDialog
          handleClose={() => setSuccessOpen(false)}
          open={isSuccessOpen}
          bannerText={successBannerText}
          title={successTitleText}
        >
          {successModal}
        </KakarikoDialog>
      )}
    </>
  );
};
